import type { PluginDefinition, PluginScore, PluginSettings } from "../../types";

export interface RolePlayConfig {
    display_name: string;
    description: string;
    opening_message: string;
    turn_limit: number;
    /** Author-facing only; absent from the config a student receives. */
    persona_prompt?: string;
    rubrics?: RolePlayRubric[];
}

export interface RolePlayRubric {
    name: string;
    description: string;
    max_score: number;
}

export interface RolePlayRubricScore {
    name: string;
    points: number;
    explanation: string;
}

export interface RolePlayScore extends PluginScore {
    rubrics: RolePlayRubricScore[];
}

export interface RolePlayPayload {
    messages: Array<{ role: "user" | "assistant"; content: string; at?: string }>;
    turns_used: number;
    turn_limit: number;
}

/** Matches SCORE_PRECISION in the node grader, so half-points never total to 7.500000000000001. */
const SCORE_PRECISION = 100;

const roundScore = (value: number): number => Math.round(value * SCORE_PRECISION) / SCORE_PRECISION;

export const totalRubricScore = (rubrics: RolePlayRubric[] = []): number =>
    roundScore(rubrics.reduce((total, rubric) => total + (rubric.max_score || 0), 0));

export const totalRubricPoints = (rubrics: RolePlayRubricScore[] = []): number =>
    roundScore(rubrics.reduce((total, rubric) => total + (rubric.points || 0), 0));

export const maxScoreError = (
    settings: PluginSettings | null,
    rubrics: RolePlayRubric[] = [],
): string => {
    const total = totalRubricScore(rubrics);
    const maxScore = settings?.max_score ?? 0;
    if (!settings?.graded || maxScore === total) return "";

    return `Max score is ${maxScore} but the criteria add up to ${total} — set the max score to ${total}.`;
};

export const DEFAULT_TURN_LIMIT = 10;
export const MAX_TURN_LIMIT = 50;
export const DEFAULT_RUBRIC_MAX_SCORE = 5;

export const ROLE_PLAY_DEFINITION: PluginDefinition<RolePlayConfig> = {
    subType: "role_play",
    label: "Role Play",
    gradeable: true,
    gradingType: "manual",
    defaultConfig: () => ({
        display_name: "",
        description: "",
        opening_message: "",
        turn_limit: DEFAULT_TURN_LIMIT,
        persona_prompt: "",
        rubrics: [],
    }),
    offeredComponents: () =>
        Promise.resolve([
            {
                label: "Role Play",
                description: "A conversation with an AI character, graded against a rubric",
                icon: "fa-comments-o",
            },
        ]),
    loadComponents: () =>
        Promise.all([
            import("./config/jnctRolePlayConfig"),
            import("./student/jnctRolePlayStudent"),
            import("./grading/jnctRolePlayGrading"),
        ]),
};
